import React, { useLayoutEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AuthBar from '../Auth/AuthBar';
import SupportBanner from '../SupportBanner/SupportBanner';
import { useSearchState } from '../../contexts/SearchStateContext';
import './SettingsLayout.css';

export const SETTINGS_BODY_CLASS = 'page-settings';

/** Bump when dwellia-logo.png is updated to bust cache */
const LOGO_VERSION = 3;

/**
 * Full-page layout for Profile and Account: header bar (logo + AuthBar),
 * back link, tab strip (Profile | Account), and scrollable content area.
 * Use activeTab="profile" or "account" and pass children as the tab content.
 */
export default function SettingsLayout({ activeTab, children }) {
  const navigate = useNavigate();
  const { searchState } = useSearchState();

  // Before paint: match Search/Home so body + #root use full-bleed layout immediately.
  // A late useEffect here left one frame of default body flex centering after leaving Search,
  // which could clip or misplace the settings shell on some viewports.
  useLayoutEffect(() => {
    document.body.classList.add(SETTINGS_BODY_CLASS);
    return () => document.body.classList.remove(SETTINGS_BODY_CLASS);
  }, []);

  const handleBackToSearch = () => {
    const addr = searchState.workingAddress || searchState.stayingAddress;
    if (addr) {
      navigate(`/search?q=${encodeURIComponent(addr)}`, {
        state: {
          workingAddress: searchState.workingAddress ?? '',
          stayingAddress: searchState.stayingAddress ?? '',
        },
      });
      return;
    }
    navigate('/search');
  };

  return (
    <div className="settings-page">
      <header className="settings-page__header-bar">
        <Link to="/" className="settings-page__logo-link" aria-label="Dwellia home">
          <img
            src={`/dwellia-logo.png?v=${LOGO_VERSION}`}
            alt=""
            className="settings-page__logo"
          />
        </Link>
        <AuthBar />
      </header>

      <div className="settings-page__toolbar">
        <button
          type="button"
          className="settings-page__back"
          onClick={handleBackToSearch}
          aria-label="Back to search"
        >
          ← Back to search
        </button>
        <nav className="settings-page__tabs" aria-label="Settings sections">
          <Link
            to="/profile"
            className={`settings-page__tab ${activeTab === 'profile' ? 'settings-page__tab--active' : ''}`}
            aria-current={activeTab === 'profile' ? 'page' : undefined}
          >
            Profile
          </Link>
          <Link
            to="/account"
            className={`settings-page__tab ${activeTab === 'account' ? 'settings-page__tab--active' : ''}`}
            aria-current={activeTab === 'account' ? 'page' : undefined}
          >
            Account
          </Link>
        </nav>
      </div>

      <main className="settings-page__main">
        <div className="settings-page__content">
          {children}
        </div>
      </main>
      <SupportBanner />
    </div>
  );
}
